import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Container principal avec le dégradé gris clair exact de l'image 1
const MobileOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(to bottom, #fefefe, #9b9b9b);
  z-index: 9999;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
  padding: 0 20px;
`;

// Titre du studio - Police exacte et style comme l'image 1
const LogoTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 8vw, 4rem);
  font-weight: 400;
  color: #303030;
  line-height: 1;
  letter-spacing: 10px;
  margin: 0 0 60px 0;
  padding: 0;
  width: 100%;
  text-align: center;
  font-family: sans-serif;
`;

// Séparateur horizontal
const Separator = styled(motion.div)`
  width: 120px;
  height: 1px;
  background: rgba(48, 48, 48, 0.3);
  margin: 30px auto;
`;

// Style pour le message d'erreur - texte en gris foncé
const Message = styled(motion.div)`
  font-size: 1rem;
  line-height: 1.6;
  width: 100%;
  max-width: 500px;
  color: #303030;
  margin: 10px 0;
  padding: 0;
  text-align: center;
`;

// Message en titre (plus grand)
const MessageTitle = styled(motion.div)`
  font-size: 1.3rem;
  line-height: 1.5;
  width: 100%;
  max-width: 500px;
  color: #303030;
  margin: 10px 0;
  padding: 0;
  text-align: center;
  font-weight: 500;
`;

// Fonction pour détecter les appareils mobiles
const checkIfMobileDevice = () => {
  return window.innerWidth < 992 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const DeviceDetector = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';
  
  // Textes en plusieurs langues
  const texts = {
    en: {
      title: "Please Use a Desktop Device",
      description: "This website is optimized for desktop viewing. Please visit us on a computer to experience our stunning website design with all its interactive features."
    },
    zh: {
      title: "请使用桌面设备",
      description: "本网站针对桌面浏览进行了优化。请使用电脑访问我们的网站，体验所有精美的设计和互动功能。"
    },
    fr: {
      title: "Veuillez Utiliser un Ordinateur",
      description: "Ce site est optimisé pour une visualisation sur ordinateur. Veuillez nous visiter sur un ordinateur pour profiter pleinement de notre design et de toutes ses fonctionnalités interactives."
    }
  };
  
  // Sélection des textes en fonction de la langue
  const content = texts[currentLanguage] || texts.en;
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIfMobileDevice());
    };
    
    // Vérification initiale
    handleResize();
    
    // Écouter les changements de taille de fenêtre
    window.addEventListener('resize', handleResize);
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Animations pour l'effet d'atterrissage
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.8,
        when: "beforeChildren",
        staggerChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };
  
  if (isMobile) {
    return (
      <AnimatePresence>
        <MobileOverlay
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <LogoTitle
            variants={itemVariants}
          >
            INO STUDIO
          </LogoTitle>
          
          <Separator variants={itemVariants} />
          
          <MessageTitle variants={itemVariants}>
            {content.title}
          </MessageTitle>
          
          <Message variants={itemVariants}>
            {content.description}
          </Message>
          
          <Separator variants={itemVariants} />
          
          {/* Afficher le message en chinois si la langue actuelle n'est pas le chinois */}
          {currentLanguage !== 'zh' && (
            <>
              <MessageTitle variants={itemVariants}>
                {texts.zh.title}
              </MessageTitle>
              
              <Message variants={itemVariants}>
                {texts.zh.description}
              </Message>
            </>
          )}
        </MobileOverlay>
      </AnimatePresence>
    );
  }
  
  return children;
};

export default DeviceDetector;